import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, AlertTriangle, AlertCircle, Clock, FileText, PenTool, Calendar, ChevronRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  expired_credential: {
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
    badge: 'bg-red-100 text-red-700',
    label: 'Expired',
  },
  expiring_credential: {
    icon: AlertTriangle,
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    label: 'Expiring Soon',
  },
  pending_review: {
    icon: FileText,
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
    label: 'Review Needed',
  },
  pending_pto: {
    icon: Calendar,
    color: 'text-violet-600',
    bg: 'bg-violet-50 border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    label: 'PTO Request',
  },
  signature_required: {
    icon: PenTool,
    color: 'text-teal-600',
    bg: 'bg-teal-50 border-teal-200',
    badge: 'bg-teal-100 text-teal-700',
    label: 'Signature Required',
  },
  booking_email_failed: {
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50 border-red-200',
    badge: 'bg-red-100 text-red-700',
    label: 'Email Failed',
  },
};

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const itemsRes = await axios.get(`${API}/notifications/my/items`, { headers });
      setItems(itemsRes.data.items || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/my/dismiss-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setItems([]);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    } finally {
      setClearing(false);
    }
  };

  const count = (t) => items.filter(i => i.type === t).length;
  const filteredItems = filter === 'all'
    ? items
    : items.filter(item => item.type === filter);

  const categories = [
    { key: 'all', label: 'All', count: items.length },
    { key: 'expired_credential', label: 'Expired', count: count('expired_credential') },
    { key: 'expiring_credential', label: 'Expiring', count: count('expiring_credential') },
    { key: 'signature_required', label: 'Signatures', count: count('signature_required') },
  ];

  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid="notifications-page">
      <div className="flex items-center gap-3 mb-6">
        <Bell size={24} className="text-primary" />
        <h1 className="text-2xl font-bold tracking-tight">Notifications</h1>
        {items.length > 0 && (
          <span className="ml-2 px-2.5 py-0.5 rounded-full bg-red-500 text-white text-sm font-medium">
            {items.length}
          </span>
        )}
        <div className="ml-auto">
          {items.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleClearAll} disabled={clearing} data-testid="clear-all-notifications">
              {clearing ? 'Clearing…' : 'Clear all'}
            </Button>
          )}
        </div>
      </div>

      {/* Category filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="notification-filters">
        {categories.map(cat => (
          <Button
            key={cat.key}
            variant={filter === cat.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(cat.key)}
            className="gap-1.5"
            data-testid={`filter-${cat.key}`}
          >
            {cat.label}
            {cat.count > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${filter === cat.key ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                {cat.count}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Notification items */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading notifications...</div>
      ) : filteredItems.length === 0 ? (
        <Card className="p-12 text-center">
          <Bell size={40} className="mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground">
            {filter === 'all' ? 'No notifications' : `No ${categories.find(c => c.key === filter)?.label.toLowerCase()} notifications`}
          </p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="notification-items">
          {filteredItems.map((item, idx) => {
            const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.pending_review;
            const Icon = config.icon;
            return (
              <Card
                key={item.id || idx}
                className={`flex items-center gap-4 p-4 border cursor-pointer hover:shadow-md transition-shadow ${config.bg}`}
                onClick={() => item.link && navigate(item.link)}
                data-testid={`notification-item-${idx}`}
              >
                <div className={`flex-shrink-0 ${config.color}`}>
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.badge}`}>
                      {config.label}
                    </span>
                  </div>
                  <p className="font-medium text-sm text-foreground truncate">{item.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{item.description}</p>
                </div>
                <ChevronRight size={16} className="flex-shrink-0 text-muted-foreground" />
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
